import { Injectable, inject } from '@angular/core';
import { ReceiptFilter, ReceiptType } from '@pay/receipts/data/receipts.model';
import { ReceiptMapperService } from '@pay/receipts/services/receipt-mapper.service';
import { ReceiptsService } from '@pay/receipts/services/receipts.service';
import { ICsvData } from '@shared/sheets/models/sheets.model';
import { SheetsService } from '@shared/sheets/services/sheets.service';
import { IColumn } from '@widgets/innogy-data-table/tableDefinition/column.interface';
import { Observable } from 'rxjs';
import { expand, map, mergeMap, reduce, takeWhile } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ReceiptSheetService {
  private readonly service = inject(ReceiptsService);
  private readonly sheetsService = inject(SheetsService);
  private readonly receiptMapperService = inject(ReceiptMapperService);

  private readonly ITEMS_PER_PAGE = 1000;
  private readonly SHEET_OPTIONS = {
    adaptUserTimeZone: true,
    dateTimeColumnIndices: [0, 7],
    translateHeaders: false,
  };

  exportToSheet(
    partnerId: string,
    filter: ReceiptFilter,
    columns: IColumn[],
    receiptType: ReceiptType
  ): Observable<ICsvData> {
    const columnIds = columns.map(column => column.id);
    const columnLabels = columns.map(column => column.value);

    return this.getCsvForPage(partnerId, 0, filter, columnIds, receiptType).pipe(
      expand(data =>
        this.getCsvForPage(partnerId, data.paging.currentPage + 1, filter, columnIds, receiptType)
      ),
      takeWhile(data => data.paging.nextPageAvailable, true), // the 'true' emits one last value
      map(data => data.lines),
      reduce((acc, lines) => [...acc, ...lines]),
      mergeMap(async mergedLines => {
        const csvData: ICsvData = { headers: columnLabels, lines: mergedLines };

        await this.sheetsService.exportToExcel(csvData, null, this.SHEET_OPTIONS);
        return csvData;
      })
    );
  }

  private getCsvForPage(
    partnerId: string,
    page: number,
    filter: ReceiptFilter,
    visibleColumns: string[],
    receiptType: ReceiptType
  ): Observable<ICsvData> {
    const request = filter;
    request.page = page;
    request.itemsPerPage = this.ITEMS_PER_PAGE;
    return this.service.getReceipts(partnerId, request, receiptType).pipe(
      map(response => {
        const rows = this.receiptMapperService.mapToCsvData(response.receipts, receiptType);
        return {
          lines: rows.map(row => {
            return {
              columns: visibleColumns.map(column => SheetsService.showTextIfAvailable(row[column])),
            };
          }),
          paging: {
            ...response.paging,
            nextPageAvailable: response.paging.currentPage + 1 < response.paging.totalPages,
          },
        };
      })
    );
  }
}
